import * as THREE from "three";
import { scene } from "../core";
import { VISUAL_CONFIG } from "@shared/config";
import type { IGhostState } from "@shared/types";

/**
 * Creates a ghost mesh with optimized geometry for better performance
 */
export function createGhostMesh(ghost: IGhostState): THREE.Group {
  const group = new THREE.Group();

  // Get ghost color based on ghost ID
  let ghostColorIndex = 0;
  const lastChar = ghost.id.slice(-1);
  ghostColorIndex =
    Number.parseInt(lastChar, 16) % VISUAL_CONFIG.COLORS.GHOST_COLORS.length ||
    0;
  const colorConfig = VISUAL_CONFIG.COLORS.GHOST_COLORS[ghostColorIndex];

  // Add subtle variations to each ghost based on ID
  // This helps distinguish them when clustered together
  const idSeed = Number.parseInt(ghost.id.substring(0, 8), 16) || 0;
  const variation = new THREE.Vector3(
    (idSeed % 100) / 1000, // small X offset (±0.05)
    (idSeed % 200) / 2000, // small Y offset (±0.05)
    (idSeed % 100) / 1000 // small Z offset (±0.05)
  );

  // Vary the ghost size slightly (95%-105% of normal)
  const sizeVariation = 0.95 + (idSeed % 100) / 1000;

  // OPTIMIZATION: Reduced polygon count from 32 segments to 16
  const bodyRadius = 0.45 * sizeVariation;
  const domeGeometry = new THREE.SphereGeometry(
    bodyRadius,
    16, // REDUCED from 32
    12, // REDUCED from 32
    0,
    Math.PI * 2,
    0,
    Math.PI / 2
  );
  domeGeometry.translate(0, bodyRadius, 0);

  // Create the lower part (skirt) - OPTIMIZED: fewer points
  const skirtHeight = bodyRadius * 0.8;
  const skirtPoints = [];

  // OPTIMIZATION: Reduced wave count from 8 to 5
  const waveCount = 5;
  const waveDepth = 0.08;

  // Create top point
  skirtPoints.push(new THREE.Vector2(0, 0));
  skirtPoints.push(new THREE.Vector2(bodyRadius * 0.99, 0));

  // Create bottom points with a wavy pattern - OPTIMIZED: fewer points
  for (let i = 0; i <= waveCount; i++) {
    const angle = (i / waveCount) * Math.PI;
    const x = bodyRadius - bodyRadius * Math.sin(angle) * 0.99;
    const y = -skirtHeight - Math.sin(i * Math.PI) * waveDepth;
    skirtPoints.push(new THREE.Vector2(x, y));
  }
  skirtPoints.push(new THREE.Vector2(0, -skirtHeight));

  // OPTIMIZATION: Reduced segments from 32 to 16
  const skirtGeometry = new THREE.LatheGeometry(
    skirtPoints,
    16 // REDUCED from 32
  );

  // OPTIMIZATION: Simplified material, removed clearcoat and other expensive properties
  const hueShift = (idSeed % 20) / 100 - 0.1;
  const saturationShift = (idSeed % 30) / 100 - 0.15;
  const ghostColor = new THREE.Color(colorConfig.color);
  const hsl = { h: 0, s: 0, l: 0 };
  ghostColor.getHSL(hsl);
  ghostColor.setHSL(
    Math.max(0, Math.min(1, hsl.h + hueShift)),
    Math.max(0.1, Math.min(1, hsl.s + saturationShift)),
    hsl.l
  );

  // OPTIMIZATION: Use StandardMaterial instead of PhysicalMaterial
  const bodyMaterial = new THREE.MeshStandardMaterial({
    color: ghostColor,
    emissive: colorConfig.emissive,
    emissiveIntensity: 0.5,
    roughness: 0.3,
  });

  // Create meshes
  const domeMesh = new THREE.Mesh(domeGeometry, bodyMaterial);
  const skirtMesh = new THREE.Mesh(skirtGeometry, bodyMaterial);

  // Add to group
  group.add(domeMesh);
  group.add(skirtMesh);

  // OPTIMIZATION: Simplified eye creation with fewer segments
  const createEye = (xPos: number) => {
    const eyeGroup = new THREE.Group();
    // OPTIMIZATION: Reduced segments from 16 to 8
    const eyeGeometry = new THREE.SphereGeometry(0.14 * sizeVariation, 8, 8);
    const eyeMaterial = new THREE.MeshStandardMaterial({
      color: 0xffffff,
      emissive: 0xaaaaaa,
      emissiveIntensity: 0.3,
      roughness: 0.1,
    });
    const eyeWhite = new THREE.Mesh(eyeGeometry, eyeMaterial);
    eyeGroup.add(eyeWhite);

    // OPTIMIZATION: Reduced segments from 12 to 8
    const pupilGeometry = new THREE.SphereGeometry(0.08 * sizeVariation, 8, 8);
    const pupilMaterial = new THREE.MeshStandardMaterial({
      color: 0x000000,
      roughness: 0.1,
    });
    const pupil = new THREE.Mesh(pupilGeometry, pupilMaterial);
    pupil.position.z = 0.08 * sizeVariation;
    eyeGroup.add(pupil);

    // OPTIMIZATION: Reduced segments from 8 to 6
    const highlightGeometry = new THREE.SphereGeometry(
      0.03 * sizeVariation,
      6,
      6
    );
    const highlightMaterial = new THREE.MeshBasicMaterial({
      color: 0xffffff,
    });
    const highlight = new THREE.Mesh(highlightGeometry, highlightMaterial);
    highlight.position.set(
      0.04 * sizeVariation,
      0.04 * sizeVariation,
      0.11 * sizeVariation
    );
    eyeGroup.add(highlight);

    const eyeYPos = bodyRadius * 1.1 + variation.y * 0.5;
    const eyeZPos = bodyRadius * 0.7 + variation.z * 0.5;
    eyeGroup.position.set(xPos, eyeYPos, eyeZPos);
    eyeGroup.userData.originalZ = eyeGroup.position.z;
    eyeGroup.userData.originalY = eyeGroup.position.y;

    return eyeGroup;
  };

  // Add eyes with slight asymmetry
  const eyeOffset = variation.x * 0.1;
  const leftEye = createEye(-0.18 + eyeOffset);
  const rightEye = createEye(0.18 + eyeOffset);
  group.add(leftEye);
  group.add(rightEye);

  // OPTIMIZATION: Simplified shadow with fewer segments
  const shadowGeometry = new THREE.CircleGeometry(bodyRadius * 0.8, 12); // REDUCED from 24
  const shadowMaterial = new THREE.MeshBasicMaterial({
    color: 0x000000,
    transparent: true,
    opacity: 0.2,
    depthWrite: false,
  });
  const shadow = new THREE.Mesh(shadowGeometry, shadowMaterial);
  shadow.rotation.x = -Math.PI / 2;
  shadow.position.y = -0.01;
  group.add(shadow);

  // OPTIMIZATION: Reduced inner light intensity
  const innerGlow = new THREE.PointLight(ghostColor, 0.3, 1); // REDUCED from 0.4
  innerGlow.position.set(0, bodyRadius / 2, 0);
  group.add(innerGlow);

  // OPTIMIZATION: Simplified glow effect
  const createGhostGlow = () => {
    // OPTIMIZATION: Reduced segments from 32 to 16
    const glowGeometry = new THREE.SphereGeometry(bodyRadius * 1.1, 16, 12);

    // Modify vertices for ghost shape
    const positions = glowGeometry.getAttribute("position").array;
    for (let i = 0; i < positions.length; i += 3) {
      if (positions[i + 1] < 0) {
        positions[i + 1] = positions[i + 1] * 1.3;
        const angle = Math.atan2(positions[i], positions[i + 2]);
        const wave = Math.sin(angle * 6) * 0.05;
        positions[i + 1] -= wave;
      }
    }

    const glowMaterial = new THREE.MeshBasicMaterial({
      color: ghostColor,
      transparent: true,
      opacity: 0.1,
      side: THREE.BackSide,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });

    return new THREE.Mesh(glowGeometry, glowMaterial);
  };

  const glow = createGhostGlow();
  group.add(glow);

  // Add animation data with unique timing for each ghost
  group.userData = {
    animationOffset: Math.random() * Math.PI * 2,
    bobSpeed: 0.003 + (idSeed % 20) / 10000,
    bobHeight: 0.07 + (idSeed % 30) / 1000,
    eyeMovementRange: 0.05,
    fadeStrength: 0.7 + (idSeed % 30) / 100,
    leftEye: leftEye,
    rightEye: rightEye,
    glow: glow,
    shadow: shadow,
    originalBodyPosition: new THREE.Vector3(0, 0, 0),
    initialY: 0.5 + variation.y * 0.2,
    sizeVariation: sizeVariation,
    floatOffset: Math.random() * Math.PI * 2,
    floatSpeed: 0.002 + Math.random() * 0.001,
  };

  // Store original eye positions for animation
  leftEye.userData.originalPosition = leftEye.position.clone();
  rightEye.userData.originalPosition = rightEye.position.clone();

  // Position ghost with precise cell alignment to prevent wall clipping
  const cellX = Math.floor(ghost.x);
  const cellY = Math.floor(ghost.y);
  const offsetX = ghost.x - cellX - 0.5;
  const offsetY = ghost.y - cellY - 0.5;

  // Limit how far from center entity can be to prevent wall clipping
  const maxOffset = 0.4;
  const clampedOffsetX = Math.max(-maxOffset, Math.min(maxOffset, offsetX));
  const clampedOffsetY = Math.max(-maxOffset, Math.min(maxOffset, offsetY));

  group.position.set(
    cellX + 0.5 + clampedOffsetX,
    0.4,
    cellY + 0.5 + clampedOffsetY
  );

  group.castShadow = true;
  group.receiveShadow = true;
  scene.add(group);

  return group;
}

/**
 * Update ghost animation - called during the animation loop
 */
export function animateGhost(ghostMesh: THREE.Group, time: number): void {
  if (!ghostMesh.userData) return;

  const ud = ghostMesh.userData;

  // Only calculate these once per frame instead of per-property
  const hoverOffset =
    Math.sin(time * ud.bobSpeed + ud.animationOffset) * ud.bobHeight;
  const squash = 1 - (hoverOffset / ud.bobHeight) * 0.1;
  const rotationAmount = Math.sin(time * 0.001 + ud.animationOffset) * 0.05;

  // Batch position and scale updates together
  ghostMesh.position.y = (ud.initialY || 0.5) + hoverOffset;
  ghostMesh.scale.y = 1 / squash;
  ghostMesh.scale.x = ghostMesh.scale.z = squash;
  ghostMesh.rotation.y = rotationAmount + (ud.baseRotation || 0);

  // OPTIMIZATION: Combined eye animations
  if (ud.leftEye && ud.rightEye) {
    const eyePhase = time * 0.0015 + ud.animationOffset;
    const eyeMovement = Math.sin(eyePhase) * ud.eyeMovementRange;

    // Move pupils together in one update
    ud.leftEye.children[1].position.x = eyeMovement;
    ud.rightEye.children[1].position.x = eyeMovement;

    // OPTIMIZATION: Less frequent blink check (only when sine wave is high)
    if (Math.sin(time * 0.001 + ud.animationOffset * 10) > 0.95) {
      ud.leftEye.scale.y = ud.rightEye.scale.y = 0.1;
    } else {
      ud.leftEye.scale.y = ud.rightEye.scale.y = 1;
    }
  }

  // OPTIMIZATION: Combined shadow & glow updates
  if (ud.shadow) {
    ud.shadow.position.y = -0.01 - hoverOffset;
    const shadowOpacity =
      0.15 + Math.sin(time * 0.002 + ud.animationOffset) * 0.05;
    (ud.shadow.material as THREE.MeshBasicMaterial).opacity = shadowOpacity;
  }

  if (ud.glow) {
    const pulseIntensity =
      0.08 + Math.sin(time * 0.002 + ud.animationOffset) * 0.03;
    (ud.glow.material as THREE.MeshBasicMaterial).opacity = pulseIntensity;
  }
}

/**
 * Updates ghost eye positions based on movement direction
 */
export function updateGhostEyesDirection(
  ghostMesh: THREE.Group,
  dx: number,
  dy: number
): void {
  if (
    !ghostMesh.userData ||
    !ghostMesh.userData.leftEye ||
    !ghostMesh.userData.rightEye
  )
    return;

  const leftEye = ghostMesh.userData.leftEye;
  const rightEye = ghostMesh.userData.rightEye;

  // Calculate direction magnitude
  const magnitude = Math.sqrt(dx * dx + dy * dy);
  if (magnitude === 0) return;

  // Normalize direction
  const ndx = dx / magnitude;
  const ndy = dy / magnitude;

  // Maximum pupil displacement
  const maxDisplacement = 0.06;

  // OPTIMIZATION: Batch updates to both pupils
  for (const eye of [leftEye, rightEye]) {
    const pupil = eye.children[1];
    pupil.position.x = ndx * maxDisplacement;
    pupil.position.y = ndy * maxDisplacement * 0.5;

    // Apply rotation
    eye.rotation.x = ndy * 0.3;
    eye.rotation.y = ndx * 0.3;
  }

  // Store base rotation to add to the wobble animation
  ghostMesh.userData.baseRotation = Math.atan2(dx, dy) * 0.1;
}
